import axios from 'axios';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { HTTP_STATUS_CODE_PREFIX_REGEX } from '../../consts/url';
import { createLocalStorageStateUtils } from '../../utils/localStorage';
import {
  CONSENT_STATE_TTL_MS,
  AZURE_DEV_URL_PREFIX,
  HTTPS_PREFIX,
} from './consts';
import { AzureInstallationErrorData, ConsentState } from './types';
import type { AvailableRepository } from '../../network/httpClients/AzureDevOpsClient/api.schema';

/**
 * Validates whether a string is an acceptable Azure DevOps organization URL.
 *
 * @param value - The raw input string
 * @returns true if the trimmed value is non-empty and starts with a recognized prefix
 *
 * @example
 * isValidAzureOrgUrl('https://dev.azure.com/my-org'); // true
 * isValidAzureOrgUrl('');                             // false
 */
export const isValidAzureOrgUrl = (value: string): boolean => {
  const trimmed = value.trim();

  return (
    trimmed.length > 0 &&
    (trimmed.startsWith(AZURE_DEV_URL_PREFIX) ||
      trimmed.startsWith(HTTPS_PREFIX))
  );
};

export type ProjectGroup = {
  name: string;
  projectId: string;
  repositories: AvailableRepository[];
};

const stateUtils = createLocalStorageStateUtils<ConsentState>(
  LOCAL_STORAGE_KEYS.AZURE_INSTALLATION_STATE,
  CONSENT_STATE_TTL_MS,
);

/**
 * Builds a consent state object for CSRF protection during OAuth redirect.
 */
export const buildConsentState = (workspaceId: string): ConsentState => ({
  secret: crypto.randomUUID(),
  workspace_id: workspaceId,
  frontend_base_url: window.location.origin,
  step: 2,
  timestamp: Date.now(),
});

export const storeConsentState = stateUtils.store;
export const getConsentState = stateUtils.get;
export const validateConsentState = stateUtils.validate;
export const clearConsentState = stateUtils.clear;

/**
 * Validates that the `state` URL parameter matches the backend-generated state
 * stored in localStorage. Prevents CSRF attacks by ensuring the redirect
 * belongs to the current consent session.
 *
 * @returns true if the URL state matches the stored backendState
 */
export const isAzureStateValid = (): boolean => {
  const storedState = getConsentState();

  if (!storedState?.backendState) {
    return false;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const urlState = urlParams.get('state');

  return !!urlState && urlState === storedState.backendState;
};

export const toggleInSet = <T>(set: Set<T>, item: T): Set<T> => {
  const newSet = new Set(set);

  if (newSet.has(item)) {
    newSet.delete(item);
  } else {
    newSet.add(item);
  }

  return newSet;
};

export const toggleAllInSet = <T>(set: Set<T>, items: T[]): Set<T> => {
  const allSelected = items.every((item) => set.has(item));
  const newSet = new Set(set);

  if (allSelected) {
    items.forEach((item) => newSet.delete(item));
  } else {
    items.forEach((item) => newSet.add(item));
  }

  return newSet;
};

const extractStringMessage = (
  field: string | { message?: string } | undefined,
): string | undefined => {
  if (typeof field === 'string') return field;

  return field?.message;
};

/**
 * Parses a raw error string in the format `STATUS: {'error': '...', 'detail': "..."}`.
 *
 * Strips the optional numeric prefix (e.g. `409: `), then extracts `detail` (preferred,
 * human-readable) or `error`, tolerating both single- and double-quoted values.
 */
export const parseRawErrorString = (message: string): string | undefined => {
  const body = message.replace(HTTP_STATUS_CODE_PREFIX_REGEX, '');

  const extract = (field: string) => {
    const match = body.match(
      new RegExp(`['"]${field}['"]\\s*:\\s*(?:'([^']*)'|"([^"]*)")`),
    );

    return match?.[1] ?? match?.[2];
  };

  return extract('detail') ?? extract('error');
};

/**
 * Extracts a human-readable message from an Axios error response.
 *
 * Resolution order:
 * 1. `response.data` as a parsed object — checks `error`, `detail`, and `message` fields
 * 2. `response.data` as a raw string — parsed via `parseRawErrorString`
 * 3. `error.message` — parsed via `parseRawErrorString` (covers `"409: {...}"` shaped JS error messages)
 *
 * @returns The extracted string, or `undefined` when no message can be found.
 */
export const extractAxiosErrorMessage = (
  error: unknown,
): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;

  const data = error.response?.data as
    | AzureInstallationErrorData
    | string
    | undefined;

  if (typeof data === 'string') return parseRawErrorString(data);

  const rawFromData =
    extractStringMessage(data?.error) ??
    extractStringMessage(data?.detail) ??
    data?.message;

  const fromData =
    rawFromData !== undefined
      ? parseRawErrorString(rawFromData) ?? rawFromData
      : undefined;

  return fromData ?? parseRawErrorString(error.message);
};

export const groupReposByProject = (
  repositories: AvailableRepository[],
): ProjectGroup[] => {
  const map = new Map<string, ProjectGroup>();

  for (const repo of repositories) {
    if (!map.has(repo.projectId)) {
      map.set(repo.projectId, {
        name: repo.project,
        projectId: repo.projectId,
        repositories: [],
      });
    }

    map.get(repo.projectId)!.repositories.push(repo);
  }

  return Array.from(map.values());
};

/**
 * Decodes a URL-encoded error description string.
 *
 * Safely decodes URL-encoded characters (e.g., `%3A` → `:`, `+` → space) and
 * returns the original string if decoding fails.
 *
 * @param encoded - The URL-encoded error description string
 * @returns The decoded error description, or the original string if decoding fails
 *
 * @example
 * decodeErrorDescription('Admin+consent+failed%3A+AADSTS650052%3A+...');
 * // Returns: 'Admin consent failed: AADSTS650052: ...'
 */
export const decodeErrorDescription = (encoded: string): string => {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
};
